import { useEffect, useRef, useState } from "react";
import { LoadingSpinner } from "../LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { cn } from "~/lib/utils";

// // // //

interface Props {
    onIntersect: () => void;
    threshold?: number;
    rootMargin?: string;
    invisible?: boolean;
}

export function InfiniteScrollTracker(props: Props) {
    const {
        onIntersect,
        threshold = 1.0,
        rootMargin = "0px",
        invisible = false,
    } = props;
    const [isIntersecting, setIsIntersecting] = useState(false);
    const trackerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsIntersecting(entry.isIntersecting);
                if (entry.isIntersecting) {
                    onIntersect();
                }
            },
            { threshold, rootMargin },
        );

        const currentTracker = trackerRef.current;

        if (currentTracker) {
            observer.observe(currentTracker);
        }

        return () => {
            if (currentTracker) {
                observer.unobserve(currentTracker);
            }
        };
    }, [onIntersect, threshold, rootMargin]);

    return (
        <div
            ref={trackerRef}
            className={cn(
                "flex items-center justify-center text-muted-foreground",
                {
                    "h-10 mt-6": !invisible,
                },
            )}
            aria-hidden="true"
        >
            {!invisible && (
                <>
                    {!isIntersecting && (
                        <div className="flex items-center gap-3">
                            <p className="text-secondary-foreground tracking-wide">
                                Scroll for more
                            </p>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="text-secondary-foreground w-4 h-4"
                            />
                        </div>
                    )}

                    {isIntersecting && (
                        <div className="flex items-center gap-3">
                            <LoadingSpinner size={5} />
                            <p className="text-secondary-foreground tracking-wide">
                                Loading articles...
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
